import {
  ApartmentOutlined,
  DatabaseOutlined,
  HomeOutlined,
  OrderedListOutlined,
  FileOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Divider, Menu, MenuProps } from "antd";
import Sider from "antd/es/layout/Sider";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PulseIndicator from "../../Components/PulseIndicator";
import useAccess from "../../Hooks/useAccess";

interface DashboardSideBarProps {
  collapsed: boolean;
  isSidebarOpen: boolean;
  handleBookSession: () => void;
}

const DashboardSideBar: React.FC<DashboardSideBarProps> = ({
  collapsed,
  isSidebarOpen,
  handleBookSession,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeKey, setActiveKey] = useState<string>(location.pathname);
  const { currentSubscription, currentPlan } = useAccess();

  const hasNoActiveJobPosts = currentSubscription?.measurements?.jobPosts === 0;
  const isFreePlan = currentPlan && currentPlan.type === "free";

  const onItemClick = (path: string) => {
    navigate(path);
    setActiveKey(path);
  };

  const items: MenuProps["items"] = [
    {
      key: "/",
      icon: <HomeOutlined />,
      label: "Home",
      onClick: () => onItemClick("/"),
    },
    {
      key: "/job-posts",
      icon: <DatabaseOutlined />,
      label: (
        <span className="menu-item-with-pulse">
          Job Posts
          {hasNoActiveJobPosts && <PulseIndicator color="red" size="small" />}
        </span>
      ),
      onClick: () => onItemClick("/job-posts"),
    },
    {
      key: "/candidates",
      icon: <UserOutlined />,
      label: "Talent Pool",
      onClick: () => onItemClick("/candidates"),
    },
    {
      key: "/team-members",
      icon: <TeamOutlined />,
      label: "Team Members",
      onClick: () => onItemClick("/team-members"),
    },
    {
      key: "/organization",
      icon: <ApartmentOutlined />,
      label: "Organization",
      onClick: () => onItemClick("/organization"),
    },
    {
      key: "/interview-settings",
      icon: <OrderedListOutlined />,
      label: "Interview Settings",
      onClick: () => onItemClick("/interview-settings"),
    },
    {
      key: "/accounts",
      icon: <UserOutlined />,
      label: "Account",
      onClick: () => onItemClick("/accounts"),
    },
    {
      key: "/resources",
      icon: <FileOutlined />,
      label: "Resources",
      onClick: () => onItemClick("/resources"),
    },
  ];

  useEffect(() => {
    const isJobPost =
      location.pathname.includes("/job-posts") ||
      location.pathname.includes("/add-job-post") ||
      location.pathname.includes("/zoho/candidate/apply");

    const isAccount =
      location.pathname.includes("/accounts") ||
      location.pathname.includes("/enterprise-plan") ||
      location.pathname.includes("/transaction-details")
      || location.pathname.includes("/plan-purchase")
      || location.pathname.includes("/credit-purchase")
      || location.pathname.includes("/pricing");

    const isZohoAuthCallback = location.pathname.includes(
      "/zoho/oauth/callback"
    );
    const isLeverAuthCallback = location.pathname.includes("/lever/callback");

    const isAuthCallback = isZohoAuthCallback || isLeverAuthCallback;

    setActiveKey(
      isJobPost
        ? "/job-posts"
        : isAccount
        ? "/accounts"
        : isAuthCallback
        ? "/organization"
        : location.pathname
    );
  }, [location.pathname]);

  return (
    <Sider trigger={null} collapsible collapsed={collapsed}>
      <Menu
        theme="light"
        mode="inline"
        selectedKeys={[activeKey]}
        items={items}
      />
      {isSidebarOpen && isFreePlan && (
        <>
          <Divider />
          <div
            className="dashboard-sider-onboarding-help-section"
            style={{
              display: collapsed ? "none" : "flex",
              padding: "8px",
              marginTop: "16px",
            }}
          >
            <small style={{}}>
              Need help with your onboarding? Book a free session with our team.
            </small>
            <Button
              size="small"
              type="primary"
              block
              onClick={handleBookSession}
            >
              Book Now
            </Button>
          </div>
        </>
      )}
    </Sider>
  );
};

export default DashboardSideBar;
